import { useState, useEffect, useRef } from "react"
import { Button } from './ui/button'
import { Input } from './ui/input'
import { MessageSquare, Send, X } from "lucide-react";

export function ChatPanel({ onClose, currentScreen, isOpen }) {
    const [messages, setMessages] = useState([
        {
            id: "1",
            role: "assistant",
            content: "I'm your AI CMO. I can explain this strategy, improve your concepts, and help you decide what to test next. Ask me anything!",
        },
    ])
    const [chatInput, setChatInput] = useState("")
    const messagesListRef = useRef(null)

    useEffect(() => {
        if (isOpen && messagesListRef.current) {
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight
        }
    }, [isOpen, messages])

    const getAICMOQuestions = () => {
        switch (currentScreen) {
            case "brand-analysis":
                return [
                    "What makes a good brand profile?",
                    "How detailed should my description be?",
                    "What if I don't have a website?",
                ]
            case "brand-profile":
                return [
                    "Is my target audience too broad?",
                    "What budget should I start with?",
                    "Which goal fits my stage best?",
                ]
            case "marketing-plan":
                return [
                    "Why this strategy for my brand?",
                    "Should I do ads or organic first?",
                    "How many concepts should I test?",
                ]
            case "ad-concepts":
                return [
                    "Which concept should I start with?",
                    "Should I test ads or organic first?",
                    "What makes a good hook?",
                ]
            case "generate-ads":
                return [
                    "How will these be generated?",
                    "Can I edit them after?",
                    "What formats will I get?",
                ]
            case "video-generation":
                return [
                    "How do I improve my script?",
                    "Which voice works best?",
                    "Should I change the hook?",
                ]
            case "performance":
                return [
                    "What's a good CTR?",
                    "How do I improve my results?",
                    "What metrics matter most?",
                ]
            default:
                return [
                    "Explain my marketing strategy",
                    "What should I test first?",
                    "How can I improve results?",
                ]
        }
    }

    const handleSendMessage = (overrideText) => {
        const text = typeof overrideText === "string" ? overrideText : chatInput
        if (!text.trim()) return
        const userMessage = {
            id: Date.now().toString(),
            role: "user",
            content: text,
        }
        setMessages((prev) => [...prev, userMessage])
        setChatInput("")

        setTimeout(() => {
            const aiMessage = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: "AI CMO chat is coming soon.\n\nAsk questions about your campaigns, brand, and ad performance — and get answers based on your actual marketing.\n\nBuilt around your business. Not generic AI advice.",
            }
            setMessages((prev) => [...prev, aiMessage])
        }, 800)
    }

    return (
        <div className="marketing-chat-panel w-80 border-l flex flex-col bg-white border-gray-200 dark:bg-[#111111] dark:border-white/5 relative">
            <div className="p-4 border-b flex items-center justify-between border-gray-200 dark:border-white/5">
                <div className="flex items-center gap-2">
                    <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-500/10">
                        <MessageSquare className="w-4 h-4 text-orange-500" />
                    </div>
                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        Ask Your AI CMO
                    </h2>
                </div>
                <button
                    type="button"
                    aria-label="Close AI CMO chat"
                    onClick={onClose}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-white dark:hover:bg-white/10"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 flex flex-col relative overflow-hidden">
                {/* Coming Soon Overlay */}
                {/* <div className="absolute inset-0 z-10 flex items-center justify-center p-6 text-center pointer-events-none">
                    <div className="space-y-4 animate-in fade-in zoom-in duration-300">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-500 text-[10px] font-bold uppercase tracking-wider">
                            <Zap className="w-3 h-3 fill-orange-500" />
                            Feature Coming Soon
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">AI CMO Assistant</h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed px-2">
                                Get strategic insights and brand optimizations directly from our AI marketing expert.
                            </p>
                        </div>
                    </div>
                </div> */}

                <div ref={messagesListRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[85%] p-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${message.role === 'user'
                                    ? 'bg-orange-500 text-white font-medium'
                                    : 'bg-gray-100 text-gray-800 dark:bg-white/5 dark:text-gray-200'
                                    }`}
                            >
                                {message.content}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="p-4 border-t border-gray-200 dark:border-white/5">
                    <div className="flex gap-2">
                        <Input
                            type="text"
                            placeholder="Ask a question..."
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                            className="flex-1 h-10 rounded-xl text-base bg-white border-gray-200 text-black placeholder:text-gray-400 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
                        />
                        <Button
                            onClick={handleSendMessage}
                            size="icon"
                            className="h-10 w-10 bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="mt-3 space-y-1">
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            Try asking:
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {getAICMOQuestions().map((prompt) => (
                                <button
                                    key={prompt}
                                    onClick={() => handleSendMessage(prompt)}
                                    className="text-[11px] font-medium px-2.5 py-1.5 rounded-lg transition-colors shadow-sm bg-gray-100 text-gray-700 hover:text-gray-900 hover:bg-gray-200 border border-gray-200/50 dark:bg-white/5 dark:text-gray-300 dark:hover:text-white dark:hover:bg-white/10 dark:border-transparent"
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
